/** Equity curve sparkline — SVG-only, no chart lib.
 * Renders a filled path (green above start, red below) so users can see
 * whether the account is above or below its starting capital.
 */
import { useMemo } from "react";

export default function EquitySparkline({ curve = [], height = 60, testId = "perf-equity-sparkline" }) {
  const { path, fill, base, minV, maxV } = useMemo(() => {
    if (!curve || curve.length < 2) return { path: "", fill: "", base: 0, minV: 0, maxV: 0 };
    const w = 400, h = height;
    const start = curve[0];
    const minV = Math.min(...curve, start);
    const maxV = Math.max(...curve, start);
    const range = Math.max(maxV - minV, 1);
    const xStep = w / (curve.length - 1);
    const y = (v) => h - ((v - minV) / range) * h;
    const pts = curve.map((v, i) => [i * xStep, y(v)]);
    const path = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
    const fill = path + ` L ${w} ${h} L 0 ${h} Z`;
    return { path, fill, base: y(start), minV, maxV };
  }, [curve, height]);

  if (!path) return <div className="text-xs text-muted-foreground" data-testid={testId}>Not enough data for equity curve.</div>;
  const positive = curve[curve.length - 1] >= curve[0];
  const stroke = positive ? "var(--tw-color-signal-green, #34C759)" : "var(--tw-color-signal-red, #FF3B30)";

  return (
    <svg width="100%" viewBox={`0 0 400 ${height}`} preserveAspectRatio="none" data-testid={testId} role="img" aria-label={`Equity curve ${positive ? "positive" : "negative"}`}>
      <path d={`M 0 ${base.toFixed(1)} L 400 ${base.toFixed(1)}`} stroke="rgba(200,200,200,0.25)" strokeDasharray="2 3" fill="none"/>
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5"/>
    </svg>
  );
}
